<script setup lang="ts">
import type { IReactionType } from "@/types"
import { usePreferredReducedMotion } from "@vueuse/core"
import { LottieAnimation } from "lottie-web-vue"
import { ref } from "vue"
import { burst, heartsFloatingMultiple } from "@/assets/LottieFiles"
import { Icon } from "@/components/ui"

interface ReactionButtonProps {
  type?: IReactionType
  countPlacement?: "bottom" | "side"
  iconVariant?: "solid" | "outline"
  totalReactCount?: number
  userHasReacted?: boolean
  hideTotalReactCount?: boolean
  formatTotalReactCount?: boolean
  disabled?: boolean
  noAnimation?: boolean
}

const props = withDefaults(defineProps<ReactionButtonProps>(), {
  type: "love",
  countPlacement: "bottom",
  iconVariant: "outline",
  totalReactCount: 0,
  userHasReacted: false,
  hideTotalReactCount: false,
  formatTotalReactCount: false,
  disabled: false,
  noAnimation: false,
})

const emit = defineEmits(["onReact", "onSuperReact"])

const preferredMotion = usePreferredReducedMotion()
const animationRef = ref<InstanceType<typeof LottieAnimation> | null>(null)
function startAnimation() {
  if (!props.noAnimation && !(preferredMotion.value === "reduce")) {
    animationRef.value?.goToAndPlay(2, true)
  }
}
defineExpose({ startAnimation })

const isPressing = ref(false)
const pressTimeout = ref<NodeJS.Timeout | null>(null)

function triggerReact(type?: "super") {
  emit(type === "super" ? "onSuperReact" : "onReact")
  startAnimation()
  isPressing.value = false
}

function handleReactionPressStart() {
  isPressing.value = true
  const maxHoldTime = 900
  pressTimeout.value = setTimeout(triggerReact, maxHoldTime, "super")
}

function handleReactionPressEnd() {
  if (!isPressing.value)
    return

  if (pressTimeout.value) {
    clearTimeout(pressTimeout.value)
    pressTimeout.value = null
    triggerReact()
  }
}

function formatNumber(number: number) {
  const formatter = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  })
  return formatter.format(number)
}
</script>

<template>
  <div
    class="flex items-center" :class="[
      { 'flex-col gap-0': countPlacement === 'bottom', 'flex-row gap-2': countPlacement === 'side' },
    ]"
  >
    <button
      unselectable="on"
      class="gpx-react-button"
      :class="[
        `gpx-react-button_${type}`,
        {
          [`gpx-react-button_reacted_${type}`]: userHasReacted,
          'gpx-react-button_no-animation': noAnimation,
          'gpx-react-button_scaling': isPressing,
        },
      ]"
      @pointerdown="handleReactionPressStart"
      @pointerup="handleReactionPressEnd"
    >
      <LottieAnimation
        ref="animationRef"
        class="gpx-react-button-animation"
        :class="`gpx-react-button-animation_${type}`"
        :animation-data="type === 'love' ? heartsFloatingMultiple : burst"
        :auto-play="false"
        :loop="false"
        :speed="1.5"
      />
      <!-- Note: Can implement different icon for different react type -->
      <Icon v-if="iconVariant === 'outline' && !userHasReacted" icon="Heart" />
      <Icon v-else icon="HeartSolid" />
    </button>
    <div v-if="!hideTotalReactCount" unselectable="on" class="gpx-media-reaction-count">
      {{ formatTotalReactCount ? formatNumber(totalReactCount) : totalReactCount }}
    </div>
  </div>
</template>

<style scoped>
.gpx-media-reaction-count {
  user-select: none;
}

.gpx-react-button {
  margin: 0;
  padding: 0;
  position: relative;
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: all 1.3s ease;
  user-select: none;
}

.gpx-react-button:hover {
  background: transparent;
}

.gpx-react-button_scaling {
  transform: scale(5);
}

.gpx-react-button_reacted_love svg,
.gpx-react-button_love svg:active {
  fill: #eb8683;
  color: #eb8683;
}

.gpx-react-button-animation {
  position: absolute;
  pointer-events: none;
}

.gpx-react-button-animation_love {
  width: 12rem;
  top: -125px;
  left: -90px;
}

.gpx-react-button_no-animation:active {
  transform: scale(1);
}

@media (prefers-reduced-motion) {
  .gpx-react-button_scaling {
    transform: scale(1);
  }
}
</style>
